import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { normalizeAuthRedirect } from "@/lib/auth-redirect";
import { useAuthGetTwoFactorVerificationProviders, useAuthSendTwoFactorVerificationCode } from "@/orval/auth";
import type { SendTwoFactorCodeDTO } from "@/orval/model";
import { toast } from "sonner";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import React, { useCallback, useState } from "react";
import { Mail, Smartphone, Shield } from "lucide-react";
import { z } from "zod";

const twoFactorOptionsSearchSchema = z.object({
  email: z.string().min(1),
  redirect: z.string().optional(),
});

const providerConfig: Record<
  string,
  {
    icon: React.ReactNode;
    title: string;
    description: string;
    color: string;
  }
> = {
  Email: {
    icon: <Mail className="size-6" />,
    title: "Email",
    description: "Get your code via email",
    color: "bg-blue-500",
  },
  Phone: {
    icon: <Smartphone className="size-6" />,
    title: "SMS",
    description: "Receive code via text message",
    color: "bg-green-500",
  },
  Authenticator: {
    icon: <Shield className="size-6" />,
    title: "Authenticator App",
    description: "Use your authenticator app",
    color: "bg-purple-500",
  },
};

const TwoFactorOptions = () => {
  const { email, redirect } = Route.useSearch();
  const { data: providers, isLoading, isError } = useAuthGetTwoFactorVerificationProviders({ userNameOrEmail: email });
  const { mutateAsync: sendCodeAsync, isPending } = useAuthSendTwoFactorVerificationCode();
  const navigate = useNavigate();
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);

  const handleSelectProvider = useCallback(
    async (provider: string) => {
      setSelectedProvider(provider);
      try {
        const dto: SendTwoFactorCodeDTO = { provider, userNameOrEmail: email };
        await sendCodeAsync({ data: dto });

        toast.success(`A two factor code has been sent to your ${provider.toLowerCase()}.`);

        navigate({
          to: "/account/two-factor-code",
          search: {
            provider: provider,
            email: email,
            redirect: normalizeAuthRedirect(redirect, window.location.origin),
          },
        });
      } catch (_) {
        toast.error("Failed to send two factor code.");
        setSelectedProvider(null);
      }
    },
    [email, navigate, redirect, sendCodeAsync],
  );

  // Defensive: providers may be undefined or not an array
  const providerList = Array.isArray(providers) ? providers : [];

  return (
    <div className="flex items-center justify-center h-full flex-1">
      <Card className="max-w-full w-[28rem] m-2">
        <CardHeader>
          <CardTitle className="text-2xl">Choose Verification Method</CardTitle>
          <CardDescription>Select how you'd like to receive your two-factor authentication code</CardDescription>
        </CardHeader>
        <CardContent>
          <VerificationMethods
            providerList={providerList}
            isLoading={isLoading}
            isError={isError}
            isPending={isPending}
            selectedProvider={selectedProvider}
            onSelectProvider={handleSelectProvider}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export const Route = createFileRoute("/account/two-factor-options")({
  validateSearch: twoFactorOptionsSearchSchema,
  component: TwoFactorOptions,
});

function VerificationMethods({
  providerList,
  isLoading,
  isError,
  isPending,
  selectedProvider,
  onSelectProvider,
}: {
  providerList: string[];
  isLoading: boolean;
  isError: boolean;
  isPending: boolean;
  selectedProvider: string | null;
  onSelectProvider: (provider: string) => Promise<void>;
}) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 mb-2">Failed to load verification methods</div>
        <p className="text-sm text-muted-foreground">Please try refreshing the page</p>
      </div>
    );
  }
  if (providerList.length > 0) {
    return (
      <div className="space-y-3">
        {providerList.map((provider: string) => {
          const config = providerConfig[provider];
          if (!config) {
            return null;
          }

          const isSelected = selectedProvider === provider;
          const isLoading = isPending && isSelected;

          return (
            <button
              key={provider}
              className={`
                w-full p-4 rounded-lg border-2 transition-all duration-200
                hover:border-primary
                focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed
                ${isSelected ? "border-primary bg-primary/5" : "border-border bg-background"}
              `}
              disabled={isPending}
              onClick={() => onSelectProvider(provider)}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`
                  p-3 rounded-full text-white transition-all duration-200
                  ${config.color}
                  ${isLoading ? "animate-pulse" : ""}
                `}
                >
                  {config.icon}
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-foreground">{config.title}</h3>
                  <p className="text-sm text-muted-foreground">{config.description}</p>
                </div>
                {isLoading && (
                  <div className="flex items-center gap-2">
                    <div className="size-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm text-muted-foreground">Sending&hellip;</span>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div className="text-center py-8">
      <Shield className="mx-auto size-12 text-muted-foreground mb-4" />
      <p className="text-muted-foreground">No verification methods available</p>
      <p className="text-sm text-muted-foreground mt-1">Please contact support for assistance</p>
    </div>
  );
}
